import React, { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type {
  AaiSession, AaiSkill, CompanyDashboard, ControlEvent, DepartmentAggregate, OwnerAdoption,
} from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, Kicker, ListRow, Loading, NotMeasured, PageBody, PageHead,
  Panel, PanelHead, RouteMark, Stat, StatusChip,
} from '../../components/aa';
import { relativeTime, whenLabel } from '../partner/format';
import { ActionUpdateDrawer } from '../champion/ActionUpdateDrawer';
import { PlanSupportDrawer, DemonstrationDrawer } from '../champion/AdoptionDrawers';
import { SharePanel } from '../champion/SharePanel';
import { SessionDrawer } from './SessionDrawer';
import { ACTION_KIND_LABEL, isClosed, isOpenRespondable, payloadText, shortName, sideOf, statusOf, subjectOf } from '../champion/actionStatus';
import {
  SESSION_TYPE_LABEL, plural, programLine, readinessLine, teamName, teamsNeedingSupport, thisWeek,
  undatedSessions, upcomingSessions,
} from './trainer';
import { QuestionsForYouPanel } from '../../components/setup/HomeSetupPanels';

/*
 * The Trainer's Home: the delivery queue.
 *
 * A trainer is asked for things — a clinic for a team, a demonstration of a
 * workflow, a blocker only the program can clear — and until now nothing in
 * the product showed them. This page is that queue first: what the company has
 * asked of the trainer, then what the trainer asked of the company, then the
 * sessions on the calendar, then the teams whose readiness says they need
 * support before their work lands. Team aggregates only; no person's score.
 */

const SessionRow: React.FC<{ session: AaiSession; onOpen: () => void }> = ({ session, onOpen }) => {
  const parts: string[] = [];
  if (session.cohort_name) parts.push(session.cohort_name);
  if (session.module_title) parts.push(session.module_title);
  if (session.duration_min) parts.push(`${session.duration_min} min`);
  return (
    <ListRow
      mark={<RouteMark kind="adoption" glyph="S" size="lg" />}
      kicker={whenLabel(session.scheduled_at)}
      title={session.title}
      badge={<Chip>{SESSION_TYPE_LABEL[session.session_type] || session.session_type}</Chip>}
      foot={parts.join(' · ') || 'No cohort attached'}
      right={<Action size="sm" variant="quiet" arrow onClick={onOpen}>Run this session</Action>}
    />
  );
};

const ActionRow: React.FC<{
  event: ControlEvent;
  label: string;
  onOpen: (e: ControlEvent) => void;
}> = ({ event: e, label, onOpen }) => {
  const state = statusOf(e.status);
  let recipients = 'Nobody, record only';
  const sent = (e.payload || {}).sent_to;
  if (Array.isArray(sent) && sent.length > 0) recipients = sent.map((x) => shortName(String(x))).join(', ');
  return (
    <ListRow
      mark={<RouteMark kind="adoption" glyph={(ACTION_KIND_LABEL[e.kind] || 'A').charAt(0)} size="lg" />}
      title={subjectOf(e)}
      badge={<Chip tone={state.tone} dot>{ACTION_KIND_LABEL[e.kind] || e.kind} · {state.label}</Chip>}
      desc={payloadText(e, 'response_note') || payloadText(e, 'note') || undefined}
      meta={[
        { label: 'Team', value: teamOf(e) },
        { label: 'Raised by', value: shortName(e.actor_email), foot: relativeTime(e.created_at) },
        { label: 'Sent to', value: recipients },
      ]}
      right={<Action size="sm" variant="quiet" arrow onClick={() => onOpen(e)}>{label}</Action>}
    />
  );
};

const teamOf = (e: ControlEvent): string => {
  const value = (e.payload || {}).department;
  if (typeof value === 'string' && value) return value;
  return 'Whole company';
};

type Drawer =
  | { kind: 'update'; event: ControlEvent }
  | { kind: 'support'; team: string | null }
  | { kind: 'demo' }
  | { kind: 'session'; session: AaiSession };

const TrainerHome: React.FC = () => {
  const dashboard = useAsync<CompanyDashboard>(() => aaiApi.getDashboard(), 'Could not load company readiness.');
  const adoption = useAsync<OwnerAdoption>(() => aaiApi.getOwnerAdoption(), 'Could not load the adoption program.');
  const events = useAsync<{ events: ControlEvent[] }>(() => aaiApi.listControlEvents(), 'Could not load what has been asked of you.');
  const sessions = useAsync<{ sessions: AaiSession[] }>(() => aaiApi.listSessions(), 'Could not load the session calendar.');
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load published Quick Wins.');
  const [drawer, setDrawer] = useState<Drawer | null>(null);
  const me = localStorage.getItem('user_email') || '';

  if (dashboard.loading) return <PageBody><Loading label="Loading your delivery queue" /></PageBody>;
  if (dashboard.error || !dashboard.data) return <PageBody><ErrorNote message={dashboard.error || 'Something went wrong.'} /></PageBody>;

  const dash = dashboard.data;
  const all = events.data?.events || [];
  const askedOfMe = all.filter((e) => isOpenRespondable(e) && sideOf(e, me) === 'recipient');
  const raisedByMe = all.filter((e) => isOpenRespondable(e) && sideOf(e, me) === 'raiser');
  const closed = all.filter((e) => isClosed(e) && (sideOf(e, me) === 'raiser' || sideOf(e, me) === 'recipient')).slice(0, 6);

  const allSessions = sessions.data?.sessions || [];
  const week = thisWeek(allSessions);
  const ahead = upcomingSessions(allSessions);
  const undated = undatedSessions(allSessions);

  const teams = teamsNeedingSupport(dash);
  const teamNames = (dash.departments || []).map(teamName).filter((n) => n !== 'Unassigned');
  const priorityNames = teams.map((t) => t.department);
  const quickWins = (skills.data?.skills || []).filter((s) => s.published);
  const program = adoption.data?.program || null;
  const exceptions = adoption.data?.exceptions || [];

  const reload = () => { events.reload(); sessions.reload(); adoption.reload(); };

  let readinessValue: React.ReactNode = <NotMeasured reason="Protected" />;
  const companyPct = dash.company.maturity?.maturity_pct;
  const companySuppressed = dash.company.suppressed || Boolean(dash.company.maturity?.suppressed);
  if (!companySuppressed && companyPct !== null && companyPct !== undefined) readinessValue = `${companyPct}%`;

  const teamRow = (t: DepartmentAggregate) => (
    <ListRow
      key={t.department}
      mark={<RouteMark kind="adoption" size="lg" />}
      title={teamName(t)}
      badge={<Chip tone="warn" dot>Needs support</Chip>}
      desc={readinessLine(t)}
      right={(
        <div className="flex flex-wrap items-center gap-2">
          <Action size="sm" variant="quiet" onClick={() => setDrawer({ kind: 'support', team: t.department })}>Plan support</Action>
          <Action size="sm" variant="ghost" onClick={() => setDrawer({ kind: 'demo' })}>Demonstration</Action>
        </div>
      )}
    />
  );

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow="Trainer"
        title="Program delivery"
        sub={[
          plural(askedOfMe.length, 'request to answer', 'requests to answer'),
          plural(week.length, 'session this week', 'sessions this week'),
          plural(teams.length, 'team needs support', 'teams need support'),
        ].join(' · ')}
        actions={<Action icon={<CalendarDays size={14} />} to="/aai/delivery">Schedule a session</Action>}
      />

      <div className="mb-5 flex flex-wrap gap-3">
        <Stat
          className="min-w-[220px] flex-1"
          label="Asked of you"
          value={askedOfMe.length}
          tone={askedOfMe.length > 0 ? 'attention' : 'quiet'}
          evidence={askedOfMe.length > 0 ? 'Listed below, answer on the record' : 'Nothing waiting on you'}
        />
        <Stat
          className="min-w-[220px] flex-1"
          label="Sessions ahead"
          value={ahead.length}
          evidence={undated.length > 0 ? `${plural(undated.length, 'session', 'sessions')} still without a date` : 'All planned sessions carry a date'}
        />
        <Stat
          className="min-w-[220px] flex-1"
          label="Company readiness"
          value={readinessValue}
          evidence={`${dash.company.onboarded_count} of ${dash.enrolled} people onboarded`}
        />
      </div>

      <Panel className="mb-5">
        <PanelHead
          kicker="Program"
          title={program?.name || 'No program set up'}
          desc={programLine(program)}
          action={<Action size="sm" variant="quiet" arrow to="/aai/curriculum">Open program</Action>}
        />
        {exceptions.length > 0 && (
          <Divided>
            {exceptions.map((x) => (
              <ListRow
                key={x.id}
                mark={<RouteMark kind="adoption" glyph="X" size="lg" />}
                title={x.title}
                badge={<Chip tone={x.overdue ? 'err' : 'warn'} dot>{x.overdue ? 'Overdue' : 'Cannot run as planned'}</Chip>}
                desc={x.exception}
                foot={[x.cohort || 'No cohort', x.scheduled_at ? whenLabel(x.scheduled_at) : 'No date'].join(' · ')}
                right={<Action size="sm" variant="quiet" arrow to="/aai/delivery">Fix in delivery</Action>}
              />
            ))}
          </Divided>
        )}
      </Panel>

      <Panel className="mb-5">
        <PanelHead
          kicker="Needs your response"
          title={askedOfMe.length === 0 ? 'Nothing is waiting on you' : `${plural(askedOfMe.length, 'request', 'requests')} from the company`}
          desc="A department head asking for a clinic, a champion routing a shared blocker, a demonstration a team wants. Accept, propose another time, or decline with a reason. Whoever asked is notified."
        />
        {askedOfMe.length === 0 && (
          <EmptyState title="Nothing asked of you" reason="A support request or demonstration appears here the moment a head or champion sends one to the program team." />
        )}
        {askedOfMe.length > 0 && (
          <Divided>
            {askedOfMe.map((e) => <ActionRow key={e.id} event={e} label="Respond" onOpen={(ev) => setDrawer({ kind: 'update', event: ev })} />)}
          </Divided>
        )}
      </Panel>

      {raisedByMe.length > 0 && (
        <Panel className="mb-5">
          <PanelHead
            kicker="Your open requests"
            title={`${plural(raisedByMe.length, 'request', 'requests')} you raised and nobody has closed`}
            desc="Support you planned and demonstrations you scheduled. Each shows who holds it and what they have said so far."
          />
          <Divided>
            {raisedByMe.map((e) => <ActionRow key={e.id} event={e} label="Update" onOpen={(ev) => setDrawer({ kind: 'update', event: ev })} />)}
          </Divided>
        </Panel>
      )}

      <SharePanel className="mb-5" hideEmpty />

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Panel>
          <PanelHead
            kicker="Next seven days"
            title={week.length === 0 ? 'Nothing booked this week' : `${plural(week.length, 'session', 'sessions')} to run`}
            action={<Action size="sm" variant="quiet" arrow to="/aai/delivery">Whole calendar</Action>}
          />
          {week.length === 0 && (
            <EmptyState title="No session this week" reason={ahead.length > 0 ? `${plural(ahead.length, 'session is', 'sessions are')} booked further out.` : 'Generate the cadence for a cohort in Delivery and the sessions appear here.'} />
          )}
          {week.length > 0 && (
            <Divided>
              {week.map((s) => <SessionRow key={s.id} session={s} onOpen={() => setDrawer({ kind: 'session', session: s })} />)}
            </Divided>
          )}
        </Panel>

        <Panel>
          <PanelHead
            kicker="Target support"
            title={teams.length === 0 ? 'No team is under the support line' : `${plural(teams.length, 'team needs', 'teams need')} support first`}
            desc="Measured team readiness under half. Aggregates only, never individuals."
            action={<Action size="sm" variant="quiet" arrow to="/aai">AI Adoption</Action>}
          />
          {teams.length === 0 && (
            <EmptyState title="Nothing to target" reason="Every team with a reportable score sits above the support line, or their groups are still too small to report." />
          )}
          {teams.length > 0 && <Divided>{teams.map(teamRow)}</Divided>}
        </Panel>
      </div>

      {closed.length > 0 && (
        <Panel>
          <PanelHead kicker="History" title="Recently closed" desc="What you answered or closed, newest first." />
          <Divided>
            {closed.map((e) => (
              <ListRow
                key={e.id}
                mark={<RouteMark kind="adoption" glyph={(ACTION_KIND_LABEL[e.kind] || 'A').charAt(0)} size="lg" />}
                title={subjectOf(e)}
                badge={<StatusChip status="record_only" label={statusOf(e.status).label} />}
                desc={payloadText(e, 'response_note') || undefined}
                foot={`${teamOf(e)} · ${relativeTime(payloadText(e, 'responded_at') || e.created_at)}`}
                right={<Action size="sm" variant="ghost" arrow onClick={() => setDrawer({ kind: 'update', event: e })}>Open</Action>}
              />
            ))}
          </Divided>
        </Panel>
      )}

      {!events.loading && all.length === 0 && askedOfMe.length === 0 && (
        <div className="mt-2">
          <Kicker>Nothing recorded yet</Kicker>
        </div>
      )}

      {drawer?.kind === 'update' && (
        <ActionUpdateDrawer event={drawer.event} onUpdated={reload} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'support' && (
        <PlanSupportDrawer
          teams={teamNames}
          priority={priorityNames}
          initialTeam={drawer.team}
          onRecorded={reload}
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer?.kind === 'demo' && (
        <DemonstrationDrawer skills={quickWins} teams={teamNames} onRecorded={reload} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'session' && (
        <SessionDrawer session={drawer.session} onChanged={reload} onClose={() => setDrawer(null)} />
      )}
      <QuestionsForYouPanel />
    </PageBody>
  );
};

export default TrainerHome;
